/* eslint-disable camelcase */
import { camelize } from 'humps';
import qs from 'qs';
import { keys, pick, toPairs } from 'ramda';

import type {
  CamelizedQueryStrings,
  QueryStringEntry,
  QueryStringKey,
  QueryStrings,
  QueryStringsReturnType,
  QueryStringValueType,
} from './queryParams.types';

// This is a workaround for picking only the first element in case of an array
// qs.parse returns an array if the query string is repeated
const firstValue = (value: QueryStringValueType) => (Array.isArray(value) ? value[0] : value) as string;

const asNumber = (value: QueryStringValueType) => {
  const result = firstValue(value).replace(/\D/g, '');
  if (!result) return -1;

  return Number(result);
};

const asString = (value: QueryStringValueType) => `${firstValue(value)}`;

const QUERY_STRING: QueryStrings = {
  game_id: asNumber,
  identifier: asString,
  language: asString,
  limit_id: asNumber,
  operatorId: asNumber,
  table_id: asNumber,
  token: asString,
  playerId: asString,
  uid: asNumber,
} as const;

export const parsedQueryString = qs.parse(window.location.search, { ignoreQueryPrefix: true });
const allowedQueryString = pick(keys(QUERY_STRING), parsedQueryString);

const getCamelizedQueryParams = () => {
  const paramsPairs = toPairs(allowedQueryString) as QueryStringEntry[];

  const camelizedParams = (params: CamelizedQueryStrings, [ key, value ]: QueryStringEntry) => {
    (params[camelize(key)] as unknown) = QUERY_STRING[key]?.(value);
    return params;
  };

  return paramsPairs.reduce(camelizedParams, {} as CamelizedQueryStrings);
};

const toStringifiedQuery = () => qs.stringify(allowedQueryString, { addQueryPrefix: true });

const containsQueryParams = (props: QueryStringKey[]) => props.every((prop) => parsedQueryString[prop]);

export const queryParams: QueryStringsReturnType = {
  contains: containsQueryParams,
  params: getCamelizedQueryParams(),
  stringify: toStringifiedQuery,
};
